import PendingBadge from "@/components/pending-badge"
import { TinyText } from "@/components/tiny-text"
import { ErrorFlagState, Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Icon } from "@/icons/icon"
import { isFilled } from "@/ipqc/lib/utils"
import { FormParamStatus } from "@/lib/form/types"
import { cn } from "@/lib/utils"
import {
  ChangeEvent,
  FocusEvent,
  InputHTMLAttributes,
  useEffect,
  useId,
  useState,
} from "react"

export type SingleReadingProps = {
  id?: string
  label: string
  labelHelpText?: string
  type?: "text" | "number"
  value?: string
  resampledValue?: string
  inputHelpText?: string
  error?: string
  disabled?: boolean
  readOnly?: boolean
  showNaForUnresolved?: boolean
  status?: FormParamStatus
  inputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"]
  flagState?: ErrorFlagState
  isChecked?: boolean
  direction?: "row" | "column"
  className?: string
  onClickErrorFlag?: (error?: string) => void
  onClickSubmittedErrorFlag?: () => void
  onClickResampleFlag?: () => void
  onChange?: (value: string) => void
  onFocus?: (value: string) => void
}

export default function SingleReading({
  id,
  label,
  value,
  type = "text",
  inputHelpText,
  labelHelpText,
  error,
  disabled,
  showNaForUnresolved,
  status,
  flagState,
  isChecked,
  direction = "row",
  className,
  resampledValue,
  onClickErrorFlag,
  onClickSubmittedErrorFlag,
  onClickResampleFlag,
  onChange,
  onFocus,
  ...props
}: SingleReadingProps) {
  const defaultId = useId()
  const inputId = id || defaultId
  const [localValue, setLocalValue] = useState("")

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    setLocalValue(e.target.value)

    if (onChange) {
      onChange(e.target.value)
    }
  }

  function handleFocus(e: FocusEvent<HTMLInputElement>) {
    if (onFocus) {
      onFocus(e.target.value)
    }
  }

  function isDisabled() {
    if (disabled) {
      return true
    }

    if (!status) {
      return false
    }

    const disabledStatuses: FormParamStatus[] = [
      FormParamStatus.NOT_APPLICABLE,
      FormParamStatus.DISABLED,
      FormParamStatus.BREAK_DOWN,
      FormParamStatus.CHANGE_OVER,
      FormParamStatus.POWER_FAILURE,
      FormParamStatus.OTHER,
    ]

    if (disabledStatuses.includes(status)) {
      return true
    }

    return false
  }

  function showPreview(status?: FormParamStatus) {
    if (!status || status === FormParamStatus.DRAFT) {
      return false
    }
    return true
  }

  function getSubmittedBadge(__value?: any, status?: FormParamStatus) {
    if (
      status === FormParamStatus.NOT_APPLICABLE ||
      showNaForUnresolved
    ) {
      return "NA"
    }

    return "PENDING"
  }

  function shouldShowValue(status?: FormParamStatus) {
    if (status === FormParamStatus.NOT_APPLICABLE) {
      return false
    }

    return isFilled(localValue) || isFilled(resampledValue)
  }

  function getBadgeStyles(status?: FormParamStatus) {
    if (
      status === FormParamStatus.NOT_APPLICABLE ||
      showNaForUnresolved
    ) {
      return "text-button-black border-black"
    }

    return ""
  }

  useEffect(() => {
    setLocalValue(isFilled(value) ? `${value}` : "")
  }, [value])

  return (
    <div
      className={cn(
        "flex items-center gap-4 text-sm",
        direction === "column" && "flex-col gap-2 justify-start items-start",
        className
      )}
    >
      <div
        className={cn(
          direction === "row" && "basis-2/5"
        )}
      >
        <Label className="text-sm font-medium" htmlFor={inputId}>
          {label}
        </Label>
        {labelHelpText && (
          <p className="text-label-text mt-1.5 text-xs">{labelHelpText}</p>
        )}
      </div>

      {showPreview(status) ? (
        <div
          className={cn(
            "flex justify-start gap-3 items-center relative",
            direction === "row" && "basis-3/5"
          )}
        >
          {shouldShowValue(status) ? (
            <span className="font-medium">
              {isFilled(resampledValue) ? resampledValue : localValue}
            </span>
          ) : (
            <PendingBadge
              className={getBadgeStyles(status)}
              text={getSubmittedBadge(localValue, status)}
            />
          )}

          {(!!error || flagState === ErrorFlagState.Filled) && (
            <button
              className="hover:cursor-pointer"
              type="button"
              onClick={onClickSubmittedErrorFlag}
            >
              <Icon name="RedFlag" className="h-4 w-4" />
            </button>
          )}

          {isChecked && (
            <Icon
              name="Checkbox"
              className="absolute top-1/2 right-3 -translate-y-1/2"
            />
          )}

          {flagState === ErrorFlagState.Resample && (
            <button
              className="hover:cursor-pointer"
              type="button"
              onClick={onClickResampleFlag}
            >
              <Icon name="ResampleBadge" className="h-6 w-auto" />
            </button>
          )}
        </div>
      ) : (
        <div
          className={cn(
            "relative",
            direction === "column" && "w-full",
            direction === "row" && "basis-3/5"
          )}
        >
          <Input
            className="w-full"
            id={inputId}
            type={type}
            value={localValue}
            error={error}
            disabled={isDisabled()}
            errorFlagState={flagState}
            {...props}
            onClickErrorFlag={onClickErrorFlag}
            onChange={handleChange}
            onFocus={handleFocus}
          />

          {isChecked && (
            <Icon
              name="Checkbox"
              className="absolute top-1/2 right-3 -translate-y-1/2"
            />
          )}
          {inputHelpText && <TinyText text={inputHelpText} />}
          {error && <TinyText className="mt-1" error text={inputHelpText} />}
        </div>
      )}
    </div>
  )
}
